import { Box, styled, css } from '@planne-software/uni/mui/material';
import { uni } from '@planne-software/uni/v2';

import { tabletBreakpoint } from '@src/common/utils/mediaQuery';
import { uniTypography } from '@src/fonts/typography/uniTypography';

import { heroGradientShift, modalDrawerSlideUp, modalPanelEnter, overlayFadeIn } from '../../styles/animations';
import { cashbackWhatsappGreen } from '../../styles/colors';

const cashbackBrandNavy = '#131552';
const cashbackHeroGradient = `linear-gradient(160deg, ${cashbackBrandNavy}, #1e1a78, #0e2a50, #1a3a6b, ${cashbackBrandNavy})`;

const accentGlow = (alpha: number) => `rgba(87, 227, 164, ${alpha})`;

export const ModalOverlay = styled(Box)`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${uni('scale-500')};
  background: rgba(10, 10, 30, 0.72);
  animation: ${overlayFadeIn} 280ms ease-out;

  ${tabletBreakpoint} {
    align-items: flex-end;
    justify-content: stretch;
    padding: 0;
  }
`;

export const ModalPanel = styled(Box)`
  width: 100%;
  max-width: 400px;
  border-radius: ${uni('border-radius-xl')};
  overflow: hidden;
  box-shadow: 0 32px 64px rgba(0, 0, 0, 0.5), 0 0 0 1px ${accentGlow(0.2)};
  animation: ${modalPanelEnter} 480ms cubic-bezier(0.2, 0.9, 0.3, 1.05);

  ${tabletBreakpoint} {
    max-width: none;
    border-radius: ${uni('border-radius-xl')} ${uni('border-radius-xl')} 0 0;
    box-shadow: 0 -8px 40px rgba(0, 0, 0, 0.35), 0 0 0 1px ${accentGlow(0.2)};
    animation: ${modalDrawerSlideUp} 420ms cubic-bezier(0.2, 0.9, 0.3, 1);
    padding-bottom: env(safe-area-inset-bottom, 0px);
  }
`;

export const ModalHero = styled(Box)`
  position: relative;
  background: ${cashbackHeroGradient};
  background-size: 300% 300%;
  padding: ${uni('scale-900')} ${uni('scale-700')} ${uni('scale-800')};
  text-align: center;
  overflow: hidden;
  animation: ${heroGradientShift} 6s ease-in-out infinite;
`;

export const ModalHeroGlow = styled(Box)`
  position: absolute;
  left: 50%;
  top: 50%;
  width: 300px;
  height: 300px;
  margin: -150px 0 0 -150px;
  border-radius: ${uni('border-radius-round')};
  background: radial-gradient(circle, ${accentGlow(0.12)} 0%, transparent 70%);
  pointer-events: none;
`;

export const ModalCloseButton = styled('button')`
  position: absolute;
  top: ${uni('scale-350')};
  right: ${uni('scale-350')};
  z-index: 10;
  width: ${uni('scale-700')};
  height: ${uni('scale-700')};
  border: 0;
  border-radius: ${uni('border-radius-round')};
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
`;

export const ModalLogo = styled(Box)`
  position: relative;
  display: flex;
  justify-content: center;
  margin-bottom: ${uni('scale-800')};

  svg {
    display: block;
    width: 150px;
    height: auto;
  }
`;

export const ModalTitle = styled('h2')`
  position: relative;
  margin: 0;
  ${uniTypography('display-md', 'bold')}
  line-height: 1.15;
  color: ${uni('neutral-white')};
  text-wrap: balance;
`;

export const ModalDivider = styled(Box)`
  position: relative;
  background: ${cashbackBrandNavy};
  height: 1px;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-top: 2px dashed rgba(255, 255, 255, 0.1);
  }
`;

export const ModalBody = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: uni('scale-350'),
  width: '100%',
  padding: `${uni('padding-content')} ${uni('padding-content')} ${uni('scale-500')}`,
  backgroundColor: uni('bg-static-content-default'),
}));

export const ModalHintRow = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: uni('scale-300'),
  width: '100%',
}));

export const ModalHintIcon = styled(Box)<{ $variant: 'primary' | 'warning' }>(({ $variant }) => ({
  flexShrink: 0,
  width: uni('scale-1000'),
  height: uni('scale-1000'),
  borderRadius: uni('border-radius-sm'),
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: $variant === 'warning' ? uni('bg-warning-subtle-rest') : uni('bg-primary-subtle-rest'),
  color: $variant === 'warning' ? uni('fg-warning-default-rest') : uni('fg-primary-default-rest'),
}));

export const ModalHintText = styled('p')<{ $coloredHighlight?: boolean }>`
  flex: 1;
  margin: 0;
  ${uniTypography('text-sm', 'medium')}
  color: ${uni('fg-neutral-static-default-body')};

  ${({ $coloredHighlight = false }) =>
    $coloredHighlight &&
    css`
      strong {
        color: ${uni('fg-warning-default-rest')};
      }
    `}
`;

export const WhatsappButton = styled('button')`
  width: 100%;
  margin-top: ${uni('scale-150')};
  padding: ${uni('scale-400')};
  border: 0;
  border-radius: ${uni('border-radius-sm')};
  background: ${cashbackWhatsappGreen};
  color: ${uni('neutral-white')};
  font-family: ${uni('font-family-text')};
  ${uniTypography('text-sm', 'semibold')}
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${uni('scale-250')};
`;

export const DeclineButton = styled('button')`
  width: 100%;
  padding: ${uni('scale-250')};
  border: 0;
  background: transparent;
  color: ${uni('fg-neutral-static-default-body')};
  font-family: ${uni('font-family-text')};
  ${uniTypography('text-sm', 'medium')}
  cursor: pointer;
  border-radius: ${uni('border-radius-xs')};
`;
